import type { Marketplace, Money, ReOSDatabase } from "./types";

export type ConnectedMarketplace = Extract<Marketplace, "ebay" | "etsy">;

export interface ChannelPhotoSource {
  id: string;
  url: string;
  order: number;
  width?: number;
  height?: number;
}

export interface ChannelListingSnapshot {
  externalListingID: string;
  externalAccountID: string;
  title: string;
  description?: string;
  sku?: string;
  quantity: number;
  quantityNeedsReview?: boolean;
  price?: Money;
  externalURL?: string;
  categoryID?: string;
  categoryName?: string;
  condition?: string;
  status: "active" | "ended";
  photos: ChannelPhotoSource[];
  galleryComplete: boolean;
  variationPhotosNeedReview?: boolean;
  sourceUpdatedAt?: string;
  listedAt?: string;
}

export interface ConnectedListingEdit {
  version: string;
  generation: number;
  base: { title: string; description: string; price: Money };
  values: { title: string; description: string; price: Money };
  state: "draft" | "updating" | "pending" | "failed";
  operationID?: string;
  error?: string;
  updatedAt: string;
}

export type ChannelChangedField = "title" | "description" | "price" | "quantity" | "photos" | "status";

export type ChannelChangeValues = Partial<Omit<ChannelListingSnapshot, "photos">> & { photos?: Array<{ id: string }> };

export interface ChannelObservation {
  id: string;
  workspaceID: string;
  marketplaceAccountID: string;
  generation: number;
  marketplace: ConnectedMarketplace;
  externalListingID: string;
  revision: number;
  fingerprint: string;
  current: ChannelListingSnapshot;
  previous?: ChannelListingSnapshot;
  changes: Array<{ id: string; fields: ChannelChangedField[]; before: ChannelChangeValues; after: ChannelChangeValues; detectedAt: string; origin: "channel" | "listwork" }>;
  detectedAt: string;
  checkedAt: string;
  lastHydratedAt?: string;
  seenInScanID?: string;
  stockDecision?: { revision: number; itemVersion: string; choice: "keep" | "counted"; actorUserID: string; decidedAt: string; operationID?: string; requestVersion?: string };
  draftDecision?: { revision: number; listingVersion: string; choice: "keep" | "use_channel"; actorUserID: string; decidedAt: string };
  ignoredAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** A committed run doubles as a transactional outbox entry until dispatched. */
export interface ChannelSyncRun {
  id: string;
  workspaceID: string;
  marketplaceAccountID: string;
  generation: number;
  marketplace: ConnectedMarketplace;
  status: "queued" | "checking" | "complete" | "partial" | "failed" | "cancelled";
  mode: "full" | "incremental";
  page: number;
  pendingListingIDs: string[];
  pendingEndChecks?: string[];
  unavailableListingIDs?: string[];
  scanned: number;
  totalEntries?: number;
  windowFrom?: string;
  windowTo: string;
  lease?: string;
  leaseUntil?: string;
  dispatchedAt?: string;
  attempts: number;
  error?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface ChannelPhotoEntry {
  source: ChannelPhotoSource;
  sourceKey: string;
  status: "queued" | "saving" | "saved" | "duplicate" | "deleted" | "failed" | "unavailable" | "blocked";
  attempts: number;
  error?: string;
  assetID?: string;
  storageKey?: string;
  provider?: "r2" | "s3";
  photoID?: string;
  checksum?: string;
  timings?: { downloadMs?: number; storageMs?: number; scanMs?: number };
}

export interface ChannelPhotoJob {
  id: string;
  operationID: string;
  workspaceID: string;
  actorUserID: string;
  marketplaceAccountID: string;
  generation: number;
  marketplace: ConnectedMarketplace;
  externalListingID: string;
  itemID: string;
  listingID: string;
  reason: "new_import" | "repair" | "add";
  status: "draft" | "queued" | "saving" | "complete" | "partial" | "failed" | "cancelled";
  entries: ChannelPhotoEntry[];
  galleryDiscovered: boolean;
  itemVersion: string;
  galleryVersion?: string;
  excluded: number;
  existingPhotoIssues?: Array<{ photoID: string; reason: "missing_record" | "unverified_object" }>;
  lastFailure?: { stage: "source" | "download" | "reservation" | "storage" | "scan" | "attachment"; category: string; at: string };
  error?: string;
  lease?: string;
  leaseUntil?: string;
  dispatchedAt?: string;
  attempts: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChannelActivityPage {
  accountID: string;
  observations: ChannelObservation[];
  photoJobs: ChannelPhotoJob[];
  latestCheck?: ChannelSyncRun;
  lastSuccessfulCheckAt?: string;
  counts: { new: number; photos: number; stock: number; activity: number };
  nextCursor?: string;
  enabled: { observations: boolean; projection: boolean; importPhotos: boolean; repair: boolean };
  preferences?: { importPhotos: boolean };
}

export function observedListing(database: ReOSDatabase, observation: ChannelObservation) {
  return database.marketplaceListings.find((listing) => listing.workspaceID === observation.workspaceID && listing.marketplaceAccountID === observation.marketplaceAccountID && listing.externalListingID === observation.externalListingID && listing.externalAccountID === observation.current.externalAccountID);
}

export function hasStockDifference(database: ReOSDatabase, observation: ChannelObservation) {
  const listing = observedListing(database, observation);
  const item = database.inventoryItems.find((candidate) => candidate.id === listing?.itemID && candidate.workspaceID === observation.workspaceID);
  if (!item || observation.current.status !== "active" || observation.current.quantityNeedsReview) return false;
  if (observation.stockDecision?.revision === observation.revision && observation.stockDecision.itemVersion === item.updatedAt) return false;
  return observation.current.quantity !== item.quantity;
}

export function needsChannelPhotos(database: ReOSDatabase, observation: ChannelObservation) {
  const listing = observedListing(database, observation);
  const item = database.inventoryItems.find((entry) => entry.id === listing?.itemID && entry.workspaceID === observation.workspaceID);
  if (!listing || !item || ["sold", "archived"].includes(item.status) || database.itemPhotos.some((photo) => photo.itemID === listing.itemID)) return false;
  const jobs = (database.channelPhotoJobs ?? []).filter((job) => job.workspaceID === observation.workspaceID && job.itemID === listing.itemID);
  if (jobs.some((job) => ["queued", "saving"].includes(job.status))) return false;
  return observation.current.photos.some((source) => !jobs.some((job) => job.marketplaceAccountID === observation.marketplaceAccountID && job.externalListingID === observation.externalListingID && job.entries.some((entry) => entry.source.id === source.id && ["saved", "duplicate", "deleted"].includes(entry.status) && entry.photoID && !database.itemPhotos.some((photo) => photo.id === entry.photoID))));
}

export function channelDraftDifferences(database: ReOSDatabase, observation: ChannelObservation) {
  const listing = observedListing(database, observation);
  if (!listing || !["draft", "ready", "failed"].includes(listing.status) || !observation.previous) return [];
  if (observation.draftDecision?.revision === observation.revision && observation.draftDecision.listingVersion === listing.updatedAt) return [];
  return (["title", "description", "price"] as const).filter((field) => JSON.stringify(listing[field]) !== JSON.stringify(observation.current[field]) && JSON.stringify(observation.current[field]) !== JSON.stringify(observation.previous?.[field]));
}

export function isNewChannelListing(database: ReOSDatabase, observation: ChannelObservation) {
  return !observedListing(database, observation) && observation.current.status === "active" && !observation.ignoredAt && !(database.marketplaceImportRecords ?? []).some((record) => record.workspaceID === observation.workspaceID && record.marketplace === observation.marketplace && record.externalAccountID === observation.current.externalAccountID && record.externalListingID === observation.externalListingID && record.status === "ignored");
}
